import React, { useState } from "react";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Cell,
} from "recharts";
import ThemedChartTooltip, { ThemedTooltipBox } from "../../../shared/ThemedChartTooltip";
import { fmtDays, fmtNum } from "../utils";

// ─── TimeInPositionChart ─────────────────────────────────────────────────────────
// Horizontal bars: median days per pipeline position. Spec says "where work sticks,
// usually somewhere nobody suspected." Recharts replaces the hand-rolled div bars
// (tracker §"Replace plain SVG charts with recharts").
//
// Props: { data } where data is array of { work_type, status, median_days, tasks, worst }.
//   • Combined label = work_type — status (unique per pipeline position).
//   • Sorted by median_days descending (longest stuck first).
//   • Shows top 15 by default with a "Show all" toggle (tracker v2 §"Collapse…").
//   • Longest bar highlighted amber (#f59e0b); rest blue (#32B1FF).
//   • Tooltip shows median_days and tasks count (and worst-case when present).
//   • null/undefined/[] data → "No position data yet".
const COLORS = { longest: "#f59e0b", rest: "#32B1FF" };

const TOP_N = 10;

const TimeInPositionChart = ({ data, openDrill }) => {
  const [showAll, setShowAll] = useState(false);

  // Empty or missing data — spec: show "No position data yet" when empty/missing
  if (!Array.isArray(data) || data.length === 0) {
    return (
      <div className="bg-white dark:bg-gray-900 rounded-xl shadow border border-gray-200 dark:border-gray-800 p-5">
        <p className="text-sm font-bold text-gray-800 dark:text-gray-100">Time in Position</p>
        <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">Median days per pipeline position — where work sticks.</p>
        <div className="mt-4 h-48 bg-gray-100 dark:bg-gray-800 rounded-lg flex items-center justify-center">
          <p className="text-xs text-gray-400 dark:text-gray-500 italic">No position data yet</p>
        </div>
      </div>
    );
  }

  // Combine pipeline (work_type) + position (status) into a unique display label so
  // duplicate positions across pipelines (e.g. two "Todo" bars) are distinguishable
  // (tracker: "Fix Time in Position duplicate labels"). Falls back to status alone if
  // work_type is missing. Sorted by median_days descending (longest stuck first).
  const rows = [...data]
    .map((r) => {
      const status = r.status ?? "";
      const workType = r.work_type ?? "";
      const label = workType ? `${workType} — ${status}` : status;
      return {
        ...r,
        label,
        median_days: Number(r.median_days) || 0,
        tasks: Number(r.tasks) || 0,
        worst: r.worst == null ? null : Number(r.worst),
      };
    })
    .sort((a, b) => b.median_days - a.median_days);

  const maxDays = Math.max(1, ...rows.map((r) => r.median_days));

  // Collapse to top 10 by default; expand to all on toggle (tracker v2 §"Collapse…").
  // Matches Waiting on Them so the two side-by-side bar charts show the same number
  // of bars and the same per-bar spacing (symmetrical layout).
  const visibleRows = showAll ? rows : rows.slice(0, TOP_N);

  // Dynamic height: 350px when collapsed to 10; grows with row count when expanded,
  // capped at 600px. Matches Waiting on Them (35px per row) so bars align.
  const chartHeight = showAll
    ? Math.min(600, Math.max(400, rows.length * 35))
    : 350;

  // Truncate a combined label for the y-axis tick; full text shows in the tooltip.
  const truncateLabel = (v) => {
    if (!v) return "";
    const max = 30;
    return v.length > max ? v.slice(0, max - 1) + "…" : v;
  };

  return (
    <div className="bg-white dark:bg-gray-900 rounded-xl shadow border border-gray-200 dark:border-gray-800 p-5">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <p className="text-sm font-bold text-gray-800 dark:text-gray-100">Time in Position</p>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">Median days per pipeline position — where work sticks.</p>
        </div>
        <div className="flex items-center gap-3 text-xs text-gray-600 dark:text-gray-400">
          <span className="flex items-center gap-1.5">
            <span className="inline-block w-2.5 h-2.5 rounded-sm" style={{ backgroundColor: COLORS.longest }} />
            Longest
          </span>
          <span className="flex items-center gap-1.5">
            <span className="inline-block w-2.5 h-2.5 rounded-sm" style={{ backgroundColor: COLORS.rest }} />
            Others
          </span>
        </div>
      </div>

      {/* Chart */}
      <div className="mt-4">
        <ResponsiveContainer width="100%" height={chartHeight}>
          <BarChart
            layout="vertical"
            data={visibleRows}
            margin={{ top: 5, right: 10, left: 0, bottom: 5 }}
          >
            <CartesianGrid strokeDasharray="3 3" stroke="#374151" horizontal={false} />
            <XAxis
              type="number"
              tick={{ fontSize: 12, fill: "#9CA3AF" }}
              tickLine={false}
              axisLine={{ stroke: "#374151" }}
              allowDecimals={false}
            />
            <YAxis
              type="category"
              dataKey="label"
              tick={{ fontSize: 12, fill: "#9CA3AF" }}
              tickLine={false}
              axisLine={false}
              width={210}
              tickFormatter={truncateLabel}
            />
            <ThemedChartTooltip
              cursor={{ fill: "rgba(156,163,175,0.12)" }}
              content={({ active, payload }) => {
                if (!active || !payload?.length) return null;
                const r = payload[0].payload;
                return (
                  <ThemedTooltipBox title={r.label}>
                    <div>{fmtDays(r.median_days)} median</div>
                    <div>{fmtNum(r.tasks)} task{r.tasks !== 1 ? "s" : ""}</div>
                    {r.worst != null && <div style={{ marginTop: 4 }}>worst: {fmtDays(r.worst)}</div>}
                  </ThemedTooltipBox>
                );
              }}
            />
            <Bar
              dataKey="median_days"
              radius={[0, 4, 4, 0]}
              barSize={22}
              isAnimationActive={false}
              onClick={(d) => {
                // Recharts Bar onClick passes (data, index, event); data.payload
                // is the underlying row. Drill to the clicked position.
                const row = d?.payload ?? d;
                if (row && row.status != null) openDrill?.("position", row.status);
              }}
            >
              {visibleRows.map((r, i) => (
                <Cell key={r.label || i} fill={r.median_days === maxDays ? COLORS.longest : COLORS.rest} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>

      {rows.some((r) => r.worst != null) && (
        <p className="mt-3 text-xs text-gray-400 dark:text-gray-500">
          Hover a bar for the worst-case duration.
        </p>
      )}

      {/* Expand / collapse toggle — only when there are more than TOP_N positions */}
      {rows.length > TOP_N && (
        <div className="mt-3 flex justify-center">
          <button
            type="button"
            onClick={() => setShowAll((v) => !v)}
            className="text-xs text-gray-500 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200 underline-offset-2 hover:underline focus:outline-none"
          >
            {showAll ? "Show fewer" : `Show all ${rows.length} positions`}
          </button>
        </div>
      )}
    </div>
  );
};

export default TimeInPositionChart;
